import { faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { faLock, faArrowRight, faShieldHalved, faChartLine, faBell } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import img from "../../../assets/logo_soukhya.png";
import loginUser from "../../services/login_user";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";

const Login = () => {
  const navigate = useNavigate();
  const emailReff = useRef<HTMLInputElement | null>(null);
  const passwordReff = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const login = async () => {
    if (!emailReff.current || !passwordReff.current) return;
    const email = emailReff.current.value.trim();
    const password = passwordReff.current.value.trim();

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const response = await loginUser(email, password);

      if (response && response.token) {
        localStorage.setItem("token", response.token);
        localStorage.setItem("userId", response.user?.id);
        localStorage.setItem("isLoggedIn", JSON.stringify(true));
        navigate("/", { replace: true });
      } else {
        setError("Invalid credentials. Please try again.");
      }
    } catch {
      setError("Connection error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") login();
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-[#030712] selection:bg-blue-500/30">
      {/* Dynamic Animated Background Elements */}
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-[120px] mix-blend-screen animate-pulse" style={{ animationDuration: '8s' }} />
      <div className="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] rounded-full bg-indigo-600/20 blur-[130px] mix-blend-screen animate-pulse" style={{ animationDuration: '10s' }} />
      <div className="absolute top-[20%] right-[20%] w-[300px] h-[300px] rounded-full bg-sky-500/10 blur-[90px] mix-blend-screen animate-bounce" style={{ animationDuration: '15s' }} />
      
      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] [mask-image:radial-gradient(ellipse_60%_60%_at_50%_50%,#000_10%,transparent_100%)] opacity-30" />

      {/* Main Glass Container */}
      <div 
        className={`relative z-10 w-full max-w-[1000px] mx-4 sm:mx-8 xl:mx-auto flex flex-col md:flex-row rounded-[32px] overflow-hidden transition-all duration-1000 transform ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-0"
        }`}
        style={{
          background: "linear-gradient(145deg, rgba(13, 21, 38, 0.7) 0%, rgba(13, 21, 38, 0.4) 100%)",
          backdropFilter: "blur(40px)",
          WebkitBackdropFilter: "blur(40px)",
          border: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.7), inset 0 1px 1px rgba(255, 255, 255, 0.1)",
        }}
      >
        {/* Left Branding Panel */}
        <div className="md:w-[45%] lg:w-[42%] relative p-10 flex flex-col justify-between overflow-hidden group min-h-[400px]">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-indigo-900/40 z-0 transition-transform duration-700 group-hover:scale-105" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.1),transparent_50%)] z-0" />
          
          <div className="relative z-10 flex flex-col items-start">
            <div className="p-3 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md mb-8 inline-block shadow-lg">
              <img src={img} alt="IES logo" className="h-10 w-auto object-contain drop-shadow-md" />
            </div>
            
            <h1 className="text-3xl lg:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-white to-white/70 tracking-tight mb-4 leading-tight">
              Earth Pit <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">Monitoring System</span>
            </h1>
            <p className="text-blue-100/70 text-sm md:text-base leading-relaxed font-medium max-w-[280px]">
              Empowering your infrastructure with real-time analytics and predictive maintenance.
            </p>
          </div>

          <div className="relative z-10 mt-12 space-y-5">
            {[
              { icon: faShieldHalved, title: "Secure Access", desc: "Enterprise protection" },
              { icon: faChartLine, title: "Live Analytics", desc: "Monitor instantly" },
              { icon: faBell, title: "Smart Alerts", desc: "Predictive warnings" }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-4 group/item">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 text-blue-400 transition-all duration-300 group-hover/item:bg-blue-500/20 group-hover/item:scale-110 group-hover/item:text-blue-300 shadow-[0_0_15px_rgba(59,130,246,0.1)]">
                  <FontAwesomeIcon icon={feature.icon as IconProp} className="text-sm" />
                </div>
                <div>
                  <h3 className="text-white text-sm font-semibold tracking-wide transition-colors group-hover/item:text-blue-200">{feature.title}</h3>
                  <p className="text-blue-200/50 text-xs font-medium">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="md:w-[55%] lg:w-[58%] p-10 lg:p-14 flex flex-col justify-center relative bg-[#0a0f1e]/40 backdrop-blur-sm border-l border-white/5">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/2" />
          
          <div className="w-full max-w-md mx-auto relative z-10">
            <div className="mb-8 md:mb-10 text-center md:text-left">
              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2 tracking-tight">Welcome Back</h2>
              <p className="text-slate-400 text-sm font-medium">Please sign in to your customer dashboard.</p>
            </div>

            {error && (
              <div className="mb-5 p-4 rounded-xl bg-red-500/10 border border-red-500/20 flex items-start gap-3">
                <span className="text-red-400 translate-y-0.5">⚠️</span>
                <p className="text-red-300 text-sm font-medium">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <div className="group space-y-2">
                <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider ml-1 group-focus-within:text-blue-400 transition-colors">
                  Email Address
                </label>
                <div className="relative flex items-center">
                  <FontAwesomeIcon
                    icon={faEnvelope as IconProp}
                    className="absolute left-4 text-slate-500 text-sm group-focus-within:text-blue-400 transition-colors z-10"
                  />
                  <input
                    type="email"
                    placeholder="name@company.com"
                    ref={emailReff}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-11 pr-4 py-3.5 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500/50 focus:bg-slate-900/80 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder:text-slate-600 shadow-inner"
                  />
                </div>
              </div>

              <div className="group space-y-2">
                <div className="flex justify-between items-center ml-1">
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider group-focus-within:text-blue-400 transition-colors">
                    Password
                  </label>
                  <button
                    className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors"
                    onClick={() => navigate("/forgot-password")}
                  >
                    Forgot?
                  </button>
                </div>
                <div className="relative flex items-center">
                  <FontAwesomeIcon
                    icon={faLock as IconProp}
                    className="absolute left-4 text-slate-500 text-sm group-focus-within:text-blue-400 transition-colors z-10"
                  />
                  <input
                    type="password"
                    placeholder="••••••••"
                    ref={passwordReff}
                    onKeyDown={handleKeyDown}
                    className="w-full pl-11 pr-4 py-3.5 bg-slate-900/50 border border-slate-700/50 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500/50 focus:bg-slate-900/80 focus:ring-4 focus:ring-blue-500/10 transition-all placeholder:text-slate-600 shadow-inner"
                  />
                </div>
              </div>

              <button
                onClick={login}
                disabled={isLoading}
                className="w-full mt-4 py-3.5 rounded-xl text-white font-semibold text-sm transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden"
                style={{
                  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                  boxShadow: "0 8px 20px -6px rgba(37, 99, 235, 0.6), inset 0 1px 1px rgba(255, 255, 255, 0.2)",
                }}
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out mix-blend-overlay" />
                <div className="relative flex items-center justify-center gap-2">
                  {isLoading ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Authenticating...</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In to Customer Portal</span>
                      <FontAwesomeIcon 
                        icon={faArrowRight as IconProp} 
                        className="text-sm transform group-hover:translate-x-1 transition-transform" 
                      />
                    </>
                  )}
                </div>
              </button>
            </div>

            <div className="mt-10 mt-auto pt-6 border-t border-white/5 flex text-center justify-center">
              <p className="text-slate-500 text-xs font-medium">
                © {new Date().getFullYear()} Soukhya · Intelligent Earth Pit Monitoring System
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
